"use client";

import "@/components/home/kinetic-headline.css";
import { memo, useEffect, useRef } from "react";

/**
 * KineticHeadline — a cursor-reactive headline. Letters ease away from the
 * pointer with a soft falloff and glide back home when it leaves. Subtle by
 * design: a few pixels of drift, spring-smoothed, never enough to fight the
 * reading. Disabled entirely under prefers-reduced-motion.
 */
export const KineticHeadline = memo(
    function KineticHeadline({ text, className }: { text: string; className?: string }) {
        const rootRef = useRef<HTMLHeadingElement>(null);

        useEffect(() => {
            const root = rootRef.current;
            if (!root) {
                return;
            }
            if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
                return;
            }

            const chars = Array.from(root.querySelectorAll<HTMLElement>("[data-kinetic-char]"));
            if (chars.length === 0) {
                return;
            }

            const RADIUS = 160;
            const PUSH = 10;
            const EASE = 0.14;

            const current = chars.map(() => ({ x: 0, y: 0 }));
            let centers: { x: number; y: number }[] = [];
            let pointer: { x: number; y: number } | null = null;
            let raf = 0;
            let running = false;

            // Rest centers in viewport coords; subtract the live offset so a
            // remeasure mid-animation still yields the untransformed position.
            const remeasure = () => {
                centers = chars.map((el, index) => {
                    const rect = el.getBoundingClientRect();
                    return { x: rect.left + rect.width / 2 - current[index].x, y: rect.top + rect.height / 2 - current[index].y };
                });
            };

            const step = () => {
                let settled = true;
                for (let index = 0; index < chars.length; index++) {
                    const rest = centers[index];
                    let targetX = 0;
                    let targetY = 0;
                    if (pointer && rest) {
                        const dx = rest.x - pointer.x;
                        const dy = rest.y - pointer.y;
                        const dist = Math.hypot(dx, dy);
                        if (dist < RADIUS && dist > 0.0001) {
                            const force = (1 - dist / RADIUS) ** 2 * PUSH;
                            targetX = (dx / dist) * force;
                            targetY = (dy / dist) * force;
                        }
                    }
                    const offset = current[index];
                    offset.x += (targetX - offset.x) * EASE;
                    offset.y += (targetY - offset.y) * EASE;
                    if (Math.abs(offset.x - targetX) > 0.05 || Math.abs(offset.y - targetY) > 0.05 || Math.abs(offset.x) > 0.05 || Math.abs(offset.y) > 0.05) {
                        settled = false;
                    }
                    chars[index].style.transform = `translate(${offset.x.toFixed(2)}px, ${offset.y.toFixed(2)}px)`;
                }
                if (!settled || pointer) {
                    raf = requestAnimationFrame(step);
                } else {
                    running = false;
                }
            };

            const wake = () => {
                if (!running) {
                    running = true;
                    raf = requestAnimationFrame(step);
                }
            };

            const onMove = (event: PointerEvent) => {
                pointer = { x: event.clientX, y: event.clientY };
                wake();
            };
            const onLeave = () => {
                pointer = null;
                wake();
            };

            remeasure();
            // Web fonts land after first paint and change every glyph's width.
            document.fonts?.ready.then(remeasure).catch(() => undefined);

            root.addEventListener("pointermove", onMove);
            root.addEventListener("pointerleave", onLeave);
            window.addEventListener("resize", remeasure);
            window.addEventListener("scroll", remeasure, { passive: true });
            return () => {
                cancelAnimationFrame(raf);
                root.removeEventListener("pointermove", onMove);
                root.removeEventListener("pointerleave", onLeave);
                window.removeEventListener("resize", remeasure);
                window.removeEventListener("scroll", remeasure);
            };
        }, [text]);

        const words = text.split(" ");
        return (
            <h1 ref={rootRef} className={className} aria-label={text} data-component="KineticHeadline" data-location="src/components/home/kinetic-headline.tsx">
                {words.map((word, wordIndex) => (
                    <span key={`${word}-${wordIndex}`} className="dw-kinetic__word" aria-hidden>
                        {Array.from(word).map((char, charIndex) => (
                            <span key={`${char}-${charIndex}`} data-kinetic-char className="dw-kinetic__char">{char}</span>
                        ))}
                        {wordIndex < words.length - 1 ? "\u00A0" : null}
                    </span>
                ))}
            </h1>
        );
    }
);
